<template>
    <div class="widget-component">
        <div class="widget-booking">
            <v-row class="mx-0 align-items-start" id="booking-container"
                :class="[
                    inline ? 'booking-inline' : '',
                    modal ? 'booking-modal' : 'booking-static'
                ]">
                <v-dialog v-if="modal === true"
                    v-model="dialog"
                    persistent
                    max-width="100%"
                    width="500px"
                    :retain-focus="modal"
                    attach="#booking-container"
                    :hide-overlay="inline"
                    :no-click-animation="inline"
                >

                    <template v-slot:activator="{ on, attrs }" v-if="modal">
                        <v-card
                            width="100%"
                            outlined
                            :class="inline ? '' : 'pa-5'"
                            tile
                        >
                            <v-row align="center" v-if="!alreadyBooked">
                                <v-col cols="12" sm="12" md="12" v-if="titleText"
                                    :lg="portrait ? 12 : 6"
                                    :class="portrait ? 'pb-0' : ''">
                                    <div class="fs-1125 font-weight-bold">{{ titleText }}</div>
                                </v-col>

                                <v-col cols="12" sm="12" md="12"
                                    :lg="!titleText || titleText && portrait ? 12 : 6">
                                    <v-btn
                                        depressed
                                        tile
                                        x-large
                                        :icon="icon ? true : false"
                                        :block="block ? true : false"
                                        :color="color"
                                        class="font-weight-bold text-transform-none"
                                        :class="inline ? 'pt-2' : ''"
                                        v-bind="attrs"
                                        v-on="on"
                                    >
                                        <v-icon v-if="buttonIcon"
                                            :class="buttonText ? 'me-2' : ''">
                                            {{ buttonIcon }}
                                        </v-icon>

                                        {{ buttonText }}
                                    </v-btn>
                                </v-col>
                            </v-row>
                            <v-row align="center" v-else>
                                <v-col cols="12">
                                    <div class="fs-1125 font-weight-bold">Viewing</div>
                                </v-col>
                                <v-col cols="12" sm="12" md="12">
                                    <v-alert
                                        prominent
                                        text
                                        outlined
                                        width="100%"
                                        color="success"
                                        type="success"
                                    >
                                        <v-row align="center">
                                            <v-col class="grow">
                                                You're booked in! <br />
                                                Your viewing is at
                                                {{ bookedTime }} on {{ bookedFormattedDate }}
                                            </v-col>
                                        </v-row>
                                    </v-alert>
                                </v-col>
                            </v-row>
                        </v-card>
                    </template>

                    <Form
                        :loggedInProfile="loggedInProfile"
                        :prefill-from-customer-object="prefillFromCustomerObject"
                        :inline="inline"
                        :modal="true"
                        :listing-id="listingId"
                        :listing-slug="listingSlug"
                        :listing-name="computedTitle"
                        :listing-location="computedSubTitle"
                        :booking-code="bookingCode"
                        :skip-form="skipFormStep"
                        :show-finish-button="showFinishButton"
                        :enabled-dates="enabledDates"
                        :open-day-id="openDayIdInteger"
                        :open-day-group-id="openDayGroupIdInteger"
                        @processAppointments="processAppointments"
                        @closeDialog="closeDialog"
                        @finishedBooking="finishedBooking"
                        @booked="booked"
                    ></Form>
                </v-dialog>

                <Form v-else-if="!alreadyBooked"
                    :loggedInProfile="loggedInProfile"
                    :prefill-from-customer-object="prefillFromCustomerObject"
                    :inline="inline"
                    :modal="false"
                    :listing-id="listingId"
                    :listing-slug="listingSlug"
                    :listing-name="computedTitle"
                    :listing-location="computedSubTitle"
                    :booking-code="bookingCode"
                    :skip-form="skipFormStep"
                    :show-finish-button="showFinishButton"
                    :enabled-dates="enabledDates"
                    :open-day-id="openDayIdInteger"
                    :open-day-group-id="openDayGroupIdInteger"
                    @processAppointments="processAppointments"
                    @finishedBooking="finishedBooking"
                    @booked="booked"
                ></Form>

                <v-row align="center" v-else>
                    <v-col cols="12" sm="12" md="12">
                        <v-alert
                            prominent
                            text
                            outlined
                            width="100%"
                            color="success"
                            type="success"
                        >
                            <v-row align="center">
                                <v-col class="grow">
                                    You're already booked in! <br />
                                    Your viewing is at
                                    {{ bookedTime }} on {{ bookedFormattedDate }}
                                </v-col>
                            </v-row>
                        </v-alert>
                    </v-col>
                </v-row>
            </v-row>
        </div>
    </div>
</template>

<script>
import {computed, defineComponent, onMounted, ref, watch} from '@vue/composition-api';
    import Form from '../components/partials/micro/booking/Form';
    import sendMessageToParent from '../composables/sendMessageToParent';
    import {forEach} from "lodash";

    export default defineComponent({
        name: "BookingForm",
        components: {Form},
        props: {
            listingId: {
                type: Number,
                required: false,
                default: null
            },
            listingSlug: {
                type: String,
                required: false,
                default: null
            },
            listingName: {
                type: String,
                required: false,
                default: null
            },
            listingLocation: {
                type: String,
                required: false,
                default: null
            },
            title: {
                type: String,
                required: false,
                default: null
            },
            subTitle: {
                type: String,
                required: false,
                default: null
            },
            dates: {
                type: Array,
                required: false
            },
            minDate: {
                type: String,
                required: false
            },
            loggedInProfile: {
                type: Object,
                required: false,
                default: null
            },
            prefillFromCustomerObject: {
                type: Object,
                required: false,
                default: null
            },
            bookingCode: {
                type: String,
                required: false
            },
            skipFormStep: {
                type: Boolean,
                required: false,
                default: false
            },
            showFinishButton: {
                type: Boolean,
                required: false,
                default: false
            },

            // Design & Layout
            inline: {
                type: Boolean,
                required: false,
                default: false
            },
            modal: {
                type: Boolean,
                required: false,
                default: true
            },
            titleText: {
                type: String,
                required: false,
                default: null
            },
            buttonIcon: {
                type: String,
                required: false,
                default: null
            },
            buttonText: {
                type: String,
                required: false,
                default: 'Book Viewing'
            },
            signInUrl: {
                type: String,
                required: false,
                default: null
            },
            portrait: {
                type: Boolean,
                required: false,
                default: false
            },
            block: {
                type: Boolean,
                required: false,
                default: false
            },
            icon: {
                type: Boolean,
                required: false,
                default: false
            },
            color: {
                type: String,
                required: false,
                default: 'primary'
            },
            enabledDates: {
                type: Array,
                required: false,
                default: function() {
                    return [];
                }
            },
            openDayId: {
                type: Number,
                required: false,
                default: null
            },
            openDayGroupId: {
                type: Number,
                required: false,
                default: null
            }
        },

        setup(props, context) {
            // Component Resources
            const dialog = ref(false);
            const loginDialog = ref(false);
            const alreadyBooked = ref(false);
            const bookedTime = ref(null);
            const bookedFormattedDate = ref(null);
            const appointment = ref(null);

            const openDayIdInteger = computed(() => {
                if(props.openDayId) {
                    return parseInt(props.openDayId);
                }
                return null;
            });

            const openDayGroupIdInteger = computed(() => {
                if(props.openDayGroupId) {
                    return parseInt(props.openDayGroupId);
                }
                return null;
            });

            const computedTitle = computed(() => {
                if(props.listingName) {
                    return props.listingName;
                } else if(props.title) {
                    return props.title;
                }
                return '';
            });

            const computedSubTitle = computed(() => {
                if(props.listingLocation) {
                    return props.listingLocation;
                } else if(props.subTitle) {
                    return props.subTitle;
                }
                return '';
            });

            // Watch for dialog change
            watch(dialog, (currentValue, oldValue) => {
                if(currentValue) {
                    setTimeout(() => {
                        let dialogBox = document.querySelector('.v-dialog__content');
                        let height = dialogBox.offsetHeight + 60;
                        sendMessageToParent('setHeight', height);
                    }, 0);
                } else {
                    sendMessageToParent('setHeight', 60);
                }

            })

            function closeDialog() {
                dialog.value = false;
            }

            function openSignIn() {
                dialog.value = false;
                context.emit("showLoginDialog",true);
            }

            function finishedBooking() {
                if(dialog.value){
                    dialog.value = false;
                }

                context.emit("refreshBooking");
            }

            function booked() {
                context.emit("booked");
            }

            // Get only the available appointments
            function processAppointments(appointments) {
                let skip = false;
                forEach(appointments, function(val, key) {
                    if(val.dateTime > new Date().toISOString() && val.status != 'cancelled') {
                        if(val.application && val.application.listing && skip === false) {
                            if(val.application.listing.id) {
                                if(val.application.listing.id == props.listingId) {
                                    appointment.value = val;
                                    alreadyBooked.value = true;
                                    skip = true;
                                }
                            }
                        }
                    }
                });

                if(alreadyBooked.value === true) {
                    const datetime = new Date(appointment.value.dateTime);
                    bookedTime.value = new Intl.DateTimeFormat('en-GB',{timeZone : 'UTC',hour: 'numeric', minute: 'numeric'}).format(datetime);
                    bookedFormattedDate.value = new Intl.DateTimeFormat('en-GB').format(datetime);
                    context.emit("booked");
                }
            }

            return {
                // Variables
                dialog,
                loginDialog,
                alreadyBooked,
                bookedTime,
                bookedFormattedDate,
                openDayIdInteger,
                openDayGroupIdInteger,

                // Functions
                closeDialog,
                openSignIn,
                processAppointments,
                finishedBooking,
                booked,
                computedTitle,
                computedSubTitle
            }
        }
    });
</script>

<style lang="scss">
    .widget-booking{
        width: 100%;
        background: transparent;

        .v-application--wrap{
            min-height: auto;
        }

        #booking-container{
            &.booking-inline{
                display: block;

                .v-dialog__content{
                    position: relative;
                }

                .v-dialog{
                    margin-left: 0px;
                    margin-right: 0px;
                    box-shadow: 0px 0px 0px 0px !important;
                }

                .booking-card{
                    &:not(.static-card){
                        border: 1px solid rgba(0,0,0,.12);
                    }

                    &.static-card{
                        .v-card__text, .v-card__title{
                            padding: 0px;
                        }

                        .timeslot-slider{
                            margin-bottom: 30px;
                        }
                    }
                }

                &.booking-static{
                    .v-dialog__content{
                        justify-content: start !important;
                    }
                }
            }

            .booking-card{
                .picker-row{
                    margin: -12px -20px !important;
                }

                .v-date-picker-table{
                    button{
                        font-size: 16px;
                    }
                }

                .timeslot-slider{
                    overflow: visible;

                    .row{
                        padding-bottom: 6px;
                        border-top: 1px solid rgba(0,0,0,0.2);
                    }

                    .v-window__prev, .v-window__next{
                        margin: 0px;
                        top: unset;
                        bottom: -55px;
                    }

                    .v-window__prev{
                        left: unset;
                        right: 45px;
                    }
                }

                &:not(.modal-card){
                    .timeslot-slider{
                        .v-window__prev, .v-window__next{
                            bottom: -40px;
                        }
                    }
                }
            }
        }
    }
</style>
